import React, { useState, useEffect, useContext } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { useMutation, useApolloClient } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { Header } from "@common/components";
import {
  Heading,
  Button,
  Loading,
  TextField,
  Pager,
  ThemeStore,
  CardNotification,
  Text,
  toast,
} from "@foris/avocado-suite";
import Layout from "../../../../common/layout/Layout";
import BookingTable from "./BookingTable";
import {
  ClassroomBookingPage,
  ClassroomBooking,
  ClassroomBookingStatusEnum,
  ClassroomBookingStatusPayload,
} from "../../../../models/ISchema";
import {
  classroomBookingsCount,
  classroomBookingsWithoutCount,
} from "../../graphql/bookings.query";
import { classroomBookingChangeStatusMutation } from "../../graphql/classroomBookingChangeStatus.mutation";
import { getFields, getChangeStatusFields } from "./bookingFilterVariables";
import { Types } from "../../context/search.reducer";
import { Types as RequestTypes } from "../../context/request.reducer";
import { Types as SavedRequestTypes } from "../../context/savedRequest.reducer";
import { useIsMobile } from "@common/hooks/useIsMobile";
import useBookingAccess from "@common/hooks/useBookingAccess";
import { BookingContext } from "@modules/booking/context/BookingContext";
import useContextHasIntervals from "@modules/booking/hooks/useContextHasIntervals";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useContextUrl from "@common/hooks/useContextUrl";
import BookingListFilters from "@modules/booking/components/booking-list-filters";
import { getClassroomBookingValidationMessage } from "@modules/booking/utils/getClassroomBookingValidationMessage";
import css from "./bookingList.module.scss";

const BookingList = () => {
  const { origin, scenario, contextUrl } = useContextUrl();
  const { adaptedHomeUrl } = useNavigationUrl();
  const { dispatch } = useContext(BookingContext);
  const [filterBooking, setFilterBooking] = useState<string>("");
  const [bookings, setBookings] = useState<Array<ClassroomBooking>>([]);
  const [bookingsSelectedIds, setBookingSelectedIds] = useState<Array<string>>([]);
  const [classroomBookingChangeStatus] = useMutation(classroomBookingChangeStatusMutation, {});
  const history = useHistory();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalBookings, setTotalBookings] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [activeCount, setActiveCount] = useState<number>(0);
  const [expiredCount, setExpiredCount] = useState<number>(0);
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "expired">("all");
  const [loading, setLoading] = useState<boolean>(false);
  const [orderBy, setOrderBy] = useState([
    {
      field: "createdAt",
      direction: "DESC",
    },
  ]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [shouldDebounce, setShouldDebounce] = useState(true);
  const [sizePerPage, setSizePerPage] = useState(5);
  const [isLoaded, setIsLoaded] = useState(false);
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const { isMobile } = useIsMobile();
  const client = useApolloClient();
  const { t } = useTranslation();
  const { isBookingAccesible, isAdminBooking } = useBookingAccess();

  if (!isBookingAccesible || !isAdminBooking || !origin) {
    history.replace(adaptedHomeUrl());
    return null;
  }

  const { intervals, isScenarioValid } = useContextHasIntervals();

  const getBookings = async (
    orderByFromParams = [],
    sizePage = sizePerPage,
    page = currentPage,
    isActiveFilter = activeFilter,
    isWithoutCount = false,
  ) => {
    if (orderByFromParams) setOrderBy(orderByFromParams);
    const currentOrderBy = orderByFromParams.length ? orderByFromParams : orderBy;

    const variables = {
      originId: origin,
      scenarioId: scenario,
      filter: getFields({
        filterBooking,
        page,
        sizePage,
        orderBy: currentOrderBy,
        isActive: isActiveFilter === "all" ? undefined : isActiveFilter === "active",
      }),
    };

    try {
      setLoading(true);
      const { data: countData } = !isWithoutCount
        ? await client.query({
            query: classroomBookingsCount,
            variables,
            fetchPolicy: "no-cache",
          })
        : ({} as any);
      const { data } = await client.query({
        query: classroomBookingsWithoutCount,
        variables,
        fetchPolicy: "no-cache",
      });

      const countResponse: ClassroomBookingPage = countData?.cube?.classroomBookings;
      const response: ClassroomBookingPage = data?.cube?.classroomBookings;
      setBookings([...(response?.items ?? [])]);
      setTotalBookings(response.pageInfo.total);

      if (!isWithoutCount) {
        setPendingCount(countResponse?.pendingCount);
        setActiveCount(countResponse?.activeCount);
        setExpiredCount(countResponse?.expiredCount);
      }

      setLoading(false);
      setIsLoaded(true);
    } catch (error) {
      console.error(error);
      setIsLoaded(true);
      setLoading(false);
      setBookings([]);
      setTotalBookings(0);
      setPendingCount(0);
      setActiveCount(0);
      setExpiredCount(0);
      setBookingSelectedIds([]);
    }
  };

  const handleChangeStatus = async (status: ClassroomBookingStatusEnum) => {
    try {
      setUpdatingStatus(true);
      const response = await classroomBookingChangeStatus({
        variables: {
          originId: origin,
          scenarioId: scenario,
          input: getChangeStatusFields({
            bookingIds: bookingsSelectedIds,
            status,
            comment: null,
          }),
        },
      });
      const payload: ClassroomBookingStatusPayload =
        response?.data?.cube?.classroomBookingChangeStatus;

      if (!payload?.commited) {
        toast.error(
          getClassroomBookingValidationMessage(
            t,
            payload?.validationErrors,
            "booking.booking-list.notifications.status-change-error",
          ),
          {
            autoClose: 3000,
          },
        );

        return;
      }

      const changedBookings = (payload?.bookings ?? []).filter(Boolean) as Array<ClassroomBooking>;

      let pendingCountChanged = 0;

      const newBookings = bookings.map(booking => {
        if (changedBookings.some(item => item.id === booking.id)) {
          if (booking?.status === ClassroomBookingStatusEnum.Pending) {
            pendingCountChanged++;
          }

          booking.status = status;
        }
        return booking;
      });

      setBookings([...newBookings]);
      setPendingCount(pendingCount - pendingCountChanged);
      setBookingSelectedIds([]);

      toast.success(
        t(
          status === ClassroomBookingStatusEnum.Confirmed
            ? changedBookings.length > 1
              ? "booking.booking-list.status-changed-confirmed-plural"
              : "booking.booking-list.status-changed-confirmed"
            : changedBookings.length > 1
              ? "booking.booking-list.status-changed-rejected-plural"
              : "booking.booking-list.status-changed-rejected",
        ),
        {
          autoClose: 3000,
        },
      );
    } catch (error) {
      console.error(error);
      toast.error(t("booking.booking-list.notifications.status-change-error"), {
        autoClose: 3000,
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleResetStore = () => {
    dispatch({ type: Types.ClearSearch, payload: null });
    dispatch({ type: RequestTypes.ClearRequest, payload: null });
    dispatch({ type: SavedRequestTypes.clearSavedData, payload: null });
  };

  useEffect(() => {
    handleResetStore();
  }, []);

  useEffect(() => {
    let timeout = null;

    if (shouldDebounce) {
      timeout = setTimeout(() => {
        getBookings(orderBy);
        setBookingSelectedIds([]);
      }, 300);
    } else {
      setShouldDebounce(true);
    }

    return () => timeout && clearTimeout(timeout);
  }, [filterBooking]);

  const handleFilterChange = (filter: "all" | "active" | "expired") => {
    if (filter !== activeFilter) {
      setActiveFilter(filter);
      setCurrentPage(1);
      setBookingSelectedIds([]);
      getBookings(orderBy, sizePerPage, 1, filter, true);
    }
  };

  return (
    <Layout contextSearch={false}>
      {updatingStatus && <Loading />}

      <Header
        breadcrumbItems={[
          { title: t("home.breadcrumb.current"), path: adaptedHomeUrl() },
          { title: t("booking.title") },
        ]}
        title={t("booking.title")}
      />

      <section className={css.bookingList}>
        <div className={css.bookingList_header}>
          <Heading type={isMobile ? "h4" : "h2"} className={css.headerTitle}>
            {`${t("booking.booking-list.total", {
              count: totalBookings,
            })} | ${t("booking.booking-list.pending", {
              count: pendingCount,
            })}`}
          </Heading>

          <Button
            variant="secondary"
            leftIcon="plus"
            disabled={!isScenarioValid}
            onClick={() => {
              handleResetStore();
              history.push(`/booking/search/${contextUrl}`);
            }}
          >
            {t("booking.booking-list.create-booking")}
          </Button>
        </div>

        {!!bookingsSelectedIds.length && (
          <div
            className={cx(
              css.bookingList_actions,
              themeMode === "dark" && css.bookingList_actions__dark,
            )}
          >
            <Heading type="h4">
              {bookingsSelectedIds.length}{" "}
              {bookingsSelectedIds.length === 1
                ? t("booking.booking-list.selected-requests-single")
                : t("booking.booking-list.selected-requests")}
            </Heading>

            <div className={css.actionButtons}>
              <Button
                variant="ghost"
                leftIcon="check"
                onClick={() => handleChangeStatus(ClassroomBookingStatusEnum.Confirmed)}
              >
                {t("booking.booking-list.approve")}
              </Button>

              <Button
                variant="ghost"
                leftIcon="close"
                onClick={() => handleChangeStatus(ClassroomBookingStatusEnum.Rejected)}
              >
                {t("booking.booking-list.reject")}
              </Button>
            </div>
          </div>
        )}

        <div className={css.bookingList_filters}>
          <TextField
            className={css.inputSearch}
            placeholder={t("booking.booking-list.search-by-name")}
            value={filterBooking}
            icon="search"
            onChange={event => {
              setLoading(true);
              setCurrentPage(1);
              setFilterBooking(event.target.value);
            }}
          />

          <BookingListFilters
            className={css.statusFilter}
            totalBookings={activeCount + expiredCount}
            activeCount={activeCount}
            expiredCount={expiredCount}
            activeFilter={activeFilter}
            isLoading={loading}
            handleFilterChange={handleFilterChange}
          />
        </div>

        <div className={css.bookingList_content}>
          <BookingTable
            loading={loading || !isLoaded}
            bookings={bookings}
            bookingsSelectedIds={bookingsSelectedIds}
            getBookings={getBookings}
            setBookingSelectedIds={setBookingSelectedIds}
          />

          {!loading && !!bookings?.length && (
            <Pager
              classname={{
                global: css.pager,
              }}
              page={currentPage}
              size={sizePerPage}
              total={totalBookings}
              labelRowsPerPage={t("booking.booking-list.table.rows-per-page")}
              pageSizeOptions={[5, 10, 20]}
              showRowsPerPage
              onChange={(page: number) => {
                setShouldDebounce(false);
                setCurrentPage(page);
                setBookingSelectedIds([]);
                getBookings(orderBy, sizePerPage, page, undefined, true);
              }}
              onRowsPerPageChange={size => {
                setShouldDebounce(false);
                setSizePerPage(size.value);
                setCurrentPage(1);
                setBookingSelectedIds([]);
                getBookings(orderBy, size.value, 1, undefined, true);
              }}
            />
          )}
        </div>

        {!isScenarioValid && intervals !== null && (
          <CardNotification
            className={css.bookingList_notification}
            title={t("booking.booking-list-app.notifications.scenario-outside-range.title")}
            state="error"
          >
            <Text type="sm">
              {t("booking.booking-list-app.notifications.scenario-outside-range.description")}
            </Text>
          </CardNotification>
        )}
      </section>
    </Layout>
  );
};

export default BookingList;
